'use client';

import CollectLeadForm from '@/components/collect-lead-form';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  aiAnalysisStore,
  selectAiAnalysisIsLoadingAnalysis,
  selectAiAnalysisIsLoadingRecommendedPlans,
  selectAiAnalysisShowAnalysis,
  selectAiRecommendedPlans,
  selectAiVehicleAnalysis,
  useAiAnalysisStore,
} from '@/stores/aiAnalysisStore';
import { leadGenInfoStore, selectLeadGenInfoSubmitStatus, useLeadGenInfoStore } from '@/stores/leadGenInfoStore';
import { selectSearchRequest, useSearchRequestStore } from '@/stores/searchRequestStore';
import { SubmitStatuses } from '@/util/storeUtilities';
import { AlertTriangle, ArrowLeft, ArrowRight, Bot, Check, Loader2 } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { equalityFn } from 'wired-store';
import { WARRANTYPILOT_COVERAGE_TYPES, WARRANTYPILOT_INCLUSION_CATEGORIES } from '~/dtos/plans';

export function AiVehicleAnalysis() {
  const [analysisStage, setAnalysisStage] = useState<'loading' | 'vehicle-data' | 'matched-plans'>('loading');
  const [selectedTab, setSelectedTab] = useState('issues');

  const leadGenSubmitStatus = useLeadGenInfoStore(selectLeadGenInfoSubmitStatus);

  const { isLoadingAnalysis, isLoadingRecommendedPlans, showAnalysis, vehicleAnalysis, recommendedPlans } =
    useAiAnalysisStore(
      (state) => ({
        isLoadingAnalysis: selectAiAnalysisIsLoadingAnalysis(state),
        isLoadingRecommendedPlans: selectAiAnalysisIsLoadingRecommendedPlans(state),
        showAnalysis: selectAiAnalysisShowAnalysis(state),
        vehicleAnalysis: selectAiVehicleAnalysis(state),
        recommendedPlans: selectAiRecommendedPlans(state),
      }),
      equalityFn,
    );

  const searchRequest = useSearchRequestStore(selectSearchRequest);

  useEffect(() => {
    setAnalysisStage('vehicle-data');
    aiAnalysisStore.fetchAnalysis();
    leadGenInfoStore.reset();
  }, []);

  return (
    <Dialog open={showAnalysis} onOpenChange={(open) => aiAnalysisStore.changeShowAnalysis(open)}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-[#00B67A]" />
            WarrantyPilot AI Analysis
            <span className={`text-xs px-3 py-1 rounded-full font-bold text-white bg-green-600`}>Beta</span>
          </DialogTitle>
          <DialogDescription>Our AI is analyzing your vehicle to find the best protection plans</DialogDescription>
        </DialogHeader>

        {isLoadingAnalysis && (
          <div className="py-8 flex flex-col items-center justify-center">
            {/* <div className="relative w-full h-2 bg-gray-200 rounded-full mb-6">
              <div
                className="absolute top-0 left-0 h-full bg-[#00B67A] rounded-full transition-all duration-300"
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div> */}
            <div className="flex items-center gap-3">
              <Loader2 className="h-8 w-8 text-[#00B67A] animate-spin" />
              <div>
                <p className="font-medium">Analyzing vehicle data...</p>
                <p className="text-sm text-gray-500">Identifying common issues and repair costs</p>
              </div>
            </div>
          </div>
        )}

        {!isLoadingAnalysis && isLoadingRecommendedPlans && (
          <div className="py-8 flex flex-col items-center justify-center">
            {/* <div className="relative w-full h-2 bg-gray-200 rounded-full mb-6">
              <div
                className="absolute top-0 left-0 h-full bg-[#00B67A] rounded-full transition-all duration-300"
                style={{ width: `${loadingProgress}%` }}
              ></div>
            </div> */}
            <div className="flex items-center gap-3">
              <Loader2 className="h-8 w-8 text-[#00B67A] animate-spin" />
              <div>
                <p className="font-medium">Retrieving plans...</p>
                <p className="text-sm text-gray-500">Identifying the best plans for this vehicle</p>
              </div>
            </div>
          </div>
        )}

        {!isLoadingAnalysis && !isLoadingRecommendedPlans ? (
          leadGenSubmitStatus.submitStatus !== SubmitStatuses.SUBMITTED ? (
            <CollectLeadForm cardBorder={false} useAiStoreSearchRequest={true} />
          ) : null
        ) : null}

        {!isLoadingAnalysis &&
          !isLoadingRecommendedPlans &&
          leadGenSubmitStatus.submitStatus === SubmitStatuses.SUBMITTED &&
          analysisStage === 'vehicle-data' && (
            <div className="py-4">
              <div className="bg-[#00B67A]/10 p-4 rounded-lg mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="h-5 w-5 text-[#00B67A]" />
                  <h3 className="font-semibold text-lg">
                    {vehicleAnalysis.vehicle.year} {vehicleAnalysis.vehicle.make} {vehicleAnalysis.vehicle.model}{' '}
                    Analysis Complete
                  </h3>
                </div>
                <p className="text-sm text-gray-600">
                  Based on our database of vehicle reliability data, we've identified the following potential issues for
                  your {vehicleAnalysis.vehicle.year} {vehicleAnalysis.vehicle.make} {vehicleAnalysis.vehicle.model}.
                </p>
              </div>

              <Tabs value={selectedTab} onValueChange={setSelectedTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="issues">Common Issues</TabsTrigger>
                  <TabsTrigger value="costs">Potential Costs</TabsTrigger>
                </TabsList>

                <TabsContent value="issues" className="space-y-4">
                  {vehicleAnalysis.commonIssues.map((issue, index) => (
                    <Card key={index}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-500" />
                          {issue.title}
                        </CardTitle>
                        <CardDescription>
                          Typical repair cost:{' '}
                          <span className="font-medium">
                            ${issue.typicalRepairCost.min} - ${issue.typicalRepairCost.max}
                          </span>
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div>
                            <h4 className="text-sm font-medium mb-1">Common Repairs:</h4>
                            <div className="flex flex-wrap gap-2">
                              {issue.commonRepairs.map((repair, i) => (
                                <Badge key={i} variant="outline" className="bg-gray-100">
                                  {repair}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium mb-1">Sources:</h4>
                            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                              {issue.sources.map((source, i) => (
                                <span key={i}>
                                  {source}
                                  {i < issue.sources.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="costs">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Estimated 5-Year Repair Costs</CardTitle>
                      <CardDescription>
                        Without warranty coverage, you may face these potential expenses
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-medium mb-2">Total Potential Repair Costs:</h4>
                          <p className="text-2xl font-bold text-red-600">
                            $
                            {vehicleAnalysis.commonIssues
                              .reduce((total, issue) => {
                                return total + issue.typicalRepairCost.max;
                              }, 0)
                              .toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            Based on maximum estimated repair costs over 5 years
                          </p>
                        </div>

                        <div>
                          <h4 className="font-medium mb-2">Breakdown by Category:</h4>
                          <ul className="space-y-2">
                            {vehicleAnalysis.commonIssues.map((issue, index) => (
                              <li key={index} className="flex justify-between items-center">
                                <span>{issue.category}</span>
                                <span className="font-medium">
                                  ${issue.typicalRepairCost.min} - ${issue.typicalRepairCost.max}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <div className="mt-6 flex justify-center">
                <Button
                  onClick={() => setAnalysisStage('matched-plans')}
                  className="w-full max-w-md bg-[#00B67A] hover:bg-[#00A067] text-white rounded-md py-4 text-xl font-medium flex items-center justify-center"
                  disabled={isLoadingRecommendedPlans}
                >
                  Show Recommended Plans
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          )}

        {!isLoadingAnalysis && !isLoadingRecommendedPlans && analysisStage === 'matched-plans' && (
          <div className="py-4">
            <div className="mb-4">
              <Button
                onClick={() => setAnalysisStage('vehicle-data')}
                variant="ghost"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Analysis
              </Button>
            </div>

            {recommendedPlans && recommendedPlans.length > 0 ? (
              <>
                <div className="bg-[#00B67A]/10 p-4 rounded-lg mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="h-5 w-5 text-[#00B67A]" />
                    <h3 className="font-semibold text-lg">AI-Matched Protection Plans</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Based on the {vehicleAnalysis.vehicle.year} {vehicleAnalysis.vehicle.make}{' '}
                    {vehicleAnalysis.vehicle.model}'s common issues, we've identified these optimal protection plans.
                  </p>
                </div>

                <div className="space-y-4">
                  {recommendedPlans.map((planInfo, index) => (
                    <Card key={index} className={`relative ${index === 0 ? 'border-[#00B67A] border-2' : ''}`}>
                      {planInfo.coverageLevel && (
                        <div className="absolute top-0 right-0 -mt-3 mr-3">
                          <span
                            className={`text-xs px-3 py-1 rounded-full font-bold text-white ${
                              planInfo.coverageLevel === WARRANTYPILOT_COVERAGE_TYPES.ULTIMATE
                                ? 'bg-yellow-500'
                                : planInfo.coverageLevel === WARRANTYPILOT_COVERAGE_TYPES.ADVANCED
                                  ? 'bg-gray-400'
                                  : 'bg-amber-700'
                            }`}
                          >
                            {planInfo.coverageLevel === WARRANTYPILOT_COVERAGE_TYPES.ULTIMATE
                              ? '🥇 Ultimate Coverage'
                              : planInfo.coverageLevel === WARRANTYPILOT_COVERAGE_TYPES.ADVANCED
                                ? '🥈 Advanced Coverage'
                                : '🥉 Essential Coverage'}
                          </span>
                        </div>
                      )}
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-16 h-16 flex-shrink-0 flex items-center justify-center`}>
                            <div className={`flex items-center justify-center h-full w-full`}>
                              <Image
                                src={planInfo.provider.logo || '/placeholder.svg'}
                                alt={planInfo.provider.name}
                                width={64}
                                height={64}
                                className="object-contain"
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-semibold text-lg">{planInfo.name}</h3>
                                <p className="text-sm text-gray-600">
                                  <span className="block sm:inline">{planInfo.provider.name}</span>

                                  {/* Separator for provider and duration (on desktop only) */}
                                  {(planInfo.durationMonths || planInfo.durationMiles) && (
                                    <span className="hidden sm:inline"> • </span>
                                  )}

                                  {/* Duration */}
                                  {planInfo.durationMonths && planInfo.durationMonths !== 0 && (
                                    <span className="block sm:inline text-gray-600">
                                      {planInfo.durationMonths === 999999
                                        ? 'Unlimited Months'
                                        : `${planInfo.durationMonths / 12} years`}
                                    </span>
                                  )}

                                  {/* Separator between months and miles (desktop only) */}
                                  {planInfo.durationMonths &&
                                    planInfo.durationMonths !== 0 &&
                                    planInfo.durationMiles &&
                                    planInfo.durationMiles !== 0 && <span className="hidden sm:inline"> / </span>}

                                  {/* Miles */}
                                  {planInfo.durationMiles && planInfo.durationMiles !== 0 && (
                                    <span className="block sm:inline text-gray-600">
                                      {planInfo.durationMiles === 999999
                                        ? 'Unlimited Miles'
                                        : `${planInfo.durationMiles.toLocaleString()}${
                                            planInfo.isAdditionalOdometerPlan ? ' additional miles' : ' miles limit'
                                          }`}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold">${planInfo.price?.monthly}/mo</div>
                                <div className="text-sm text-gray-600">or ${planInfo.price?.upfront} upfront</div>
                              </div>
                            </div>

                            <div className="mt-3">
                              <div className="text-sm font-medium mb-1">Covers:</div>
                              <div className="flex flex-wrap gap-2">
                                {planInfo.coverage
                                  .flat()
                                  .filter((c) =>
                                    vehicleAnalysis.commonIssues
                                      .map((ci) => WARRANTYPILOT_INCLUSION_CATEGORIES[ci.category.toString()])
                                      .includes(c),
                                  )
                                  .map((item, i) => (
                                    <Badge key={i} className="bg-[#00B67A]/10 text-[#00B67A] border-[#00B67A]/20">
                                      {item}
                                    </Badge>
                                  ))}
                              </div>
                            </div>

                            <div className="mt-4 flex justify-between items-center">
                              <div className="flex items-center">
                                {planInfo.aiMatchScore ? (
                                  <span>
                                    <div className="text-sm font-medium mr-2">AI Match Score</div>
                                    <Badge className={`${index === 0 ? 'bg-[#00B67A]' : 'bg-gray-200 text-gray-800'}`}>
                                      {planInfo.aiMatchScore}%
                                    </Badge>
                                  </span>
                                ) : null}
                              </div>

                              <Button
                                asChild
                                className="bg-green-500 hover:bg-green-600 text-white rounded-md px-4 py-2 flex items-center"
                                onClick={() => aiAnalysisStore.changeShowAnalysis(false)}
                              >
                                <Link
                                  href={{
                                    pathname: `/plans/${planInfo.id}`,
                                    query: {
                                      planOptionId: planInfo.optionId,
                                      providerId: planInfo.provider.id,
                                      providerName: planInfo.provider.name,
                                      providerType: planInfo.provider.type,
                                      searchType: searchRequest.searchType,
                                      year: searchRequest.year,
                                      make: searchRequest.make,
                                      model: searchRequest.model,
                                      vin: searchRequest.vin,
                                      licensePlate: searchRequest.licensePlate,
                                      state: searchRequest.state,
                                      mileage: searchRequest.mileage,
                                    },
                                  }}
                                >
                                  View Details
                                </Link>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="mt-6 flex justify-center">
                  <Button
                    asChild
                    className="w-full max-w-md bg-[#00B67A] hover:bg-[#00A067] text-white rounded-xl py-4 text-lg font-medium flex items-center justify-center"
                    onClick={() => aiAnalysisStore.changeShowAnalysis(false)}
                  >
                    <a href="/plans" className="flex items-center justify-center">
                      Show All Plans
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <h3 className="font-semibold text-lg">No Matching Plans Found</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Based on your {vehicleAnalysis.vehicle.year} {vehicleAnalysis.vehicle.make}{' '}
                    {vehicleAnalysis.vehicle.model}'s analysis, we couldn't find any warranty plans that specifically
                    match the identified issues. This could be due to the vehicle's age, mileage, specific coverage or
                    state availability.
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
